import React, { useState, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FaRedo, FaFileImport, FaFileExport, FaExpand, FaQuestionCircle } from "react-icons/fa";
import { MdArrowDropDown } from "react-icons/md";
import html2canvas from 'html2canvas';
import { jsPDF } from "jspdf";


export default function BionicReadingConverter() {
  const [inputText, setInputText] = useState("my name is sheikh");
  const [fixation, setFixation] = useState(50);
  const [contrast, setContrast] = useState("none");
  const [showHelp, setShowHelp] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const { darkMode } = useTheme();
  const outputRef = useRef(null);

  const convertToBionic = () => {
    return inputText.split(' ').map(word => {
      if (word.length === 0) return word;
      
      const highlightLength = Math.max(1, Math.floor(word.length * fixation / 100));
      const highlighted = word.substring(0, highlightLength);
      const rest = word.substring(highlightLength);
      
      return (
        <span key={Math.random()} className="font-bold">
          {highlighted}
          <span className="font-normal">{rest}</span>
        </span>
      );
    }).reduce((acc, word, index) => {
      return acc.concat(index === 0 ? [word] : [' ', word]);
    }, []);
  };

  const resetConverter = () => {
    setInputText("");
    setFixation(50);
    setContrast("none");
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      alert("File size exceeds 5MB limit");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setInputText(e.target.result);
    };
    reader.readAsText(file);
  };

  const exportAsText = () => {
    const bionicText = inputText.split(' ').map(word => {
      if (word.length === 0) return word;
      const highlightLength = Math.max(1, Math.floor(word.length * fixation / 100));
      const highlighted = word.substring(0, highlightLength);
      const rest = word.substring(highlightLength);
      return `${highlighted}${rest}`;
    }).join(' ');

    const element = document.createElement("a");
    const file = new Blob([bionicText], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = "bionic-reading.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const exportAsPDF = async () => {
    const canvas = await html2canvas(outputRef.current);
    const imgData = canvas.toDataURL('image/png');
    
    const pdf = new jsPDF();  // Changed from "new window.jspdf.jsPDF()"
    pdf.addImage(imgData, 'PNG', 10, 10, 190, 0);
    pdf.save('bionic-reading.pdf');
  };

  const exportAsImage = async () => {
    const canvas = await html2canvas(outputRef.current);
    const link = document.createElement('a');
    link.download = 'bionic-reading.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const copyImageToClipboard = async () => {
    try {
      const canvas = await html2canvas(outputRef.current);
      canvas.toBlob(async (blob) => {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        alert('Image copied to clipboard!');
      });
    } catch (err) {
      console.error('Failed to copy image: ', err);
      alert('Failed to copy image to clipboard');
    }
  };

  const copyAsHTML = () => {
    const htmlContent = outputRef.current.innerHTML;
    navigator.clipboard.writeText(htmlContent)
      .then(() => alert('HTML copied to clipboard!'))
      .catch(() => alert('Failed to copy HTML'));
  };

  const copyAsMarkdown = () => {
    const bionicText = inputText.split(' ').map(word => {
      if (word.length === 0) return word;
      const highlightLength = Math.max(1, Math.floor(word.length * fixation / 100));
      const highlighted = word.substring(0, highlightLength);
      const rest = word.substring(highlightLength);
      return `**${highlighted}**${rest}`;
    }).join(' ');

    navigator.clipboard.writeText(bionicText)
      .then(() => alert('Markdown copied to clipboard!'))
      .catch(() => alert('Failed to copy Markdown'));
  };

  return (
    <>
      <HeaderSection toolId="bionicReadingConverter" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white dark:text-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">
              Bionic Reading Converter
            </h1>
            <p className="opacity-90">
              Convert text to bionic reading format for improved reading speed
            </p>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Help Section */}
            <div className="mb-6">
              <button 
                onClick={() => setShowHelp(!showHelp)}
                className="flex items-center text-sm text-green-600 dark:text-green-400 hover:underline"
              >
                <FaQuestionCircle className="mr-2" />
                Help: What is Bionic Reading?
              </button>
              {showHelp && (
                <div className="mt-2 p-4 bg-gray-100 dark:bg-gray-700 rounded-md">
                  <p className="text-gray-700 dark:text-gray-300">
                    Bionic Reading is a reading system that guides the eyes through text with artificial fixation points.
                    The first part of each word is highlighted to help your brain process text faster.
                  </p>
                </div>
              )}
            </div>

            {/* File Import */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Import text from file (Max 5MB)
              </label>
              <label className="inline-flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-md cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition">
                <FaFileImport className="mr-2" />
                Choose File
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".pdf,.txt"
                  onChange={handleFileImport}
                />
              </label>
            </div>

            {/* Fixation Control */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fixation (% of highlighted)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="10"
                  max="90"
                  step="5"
                  className="w-full max-w-xs"
                  value={fixation}
                  onChange={(e) => setFixation(parseInt(e.target.value))}
                />
                <span className="text-sm font-medium">
                  {fixation}% - {fixation < 30 ? 'Low' : fixation < 60 ? 'Medium' : 'High'}
                </span>
              </div>
            </div>

            {/* Contrast Control */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contrast
              </label>
              <select
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500"
                value={contrast}
                onChange={(e) => setContrast(e.target.value)}
              >
                <option value="none">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {/* Input Text */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Text
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500 h-32"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter your text here..."
              />
            </div>

            {/* Bionic Reading Output */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bionic Reading Mode
              </label>
              <div 
                ref={outputRef}
                className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 h-32 overflow-y-auto ${contrast === 'none' ? '' : contrast === 'low' ? 'contrast-75' : contrast === 'medium' ? 'contrast-100' : 'contrast-125'}`}
              >
                {inputText ? convertToBionic() : "Your bionic reading text will appear here..."}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={resetConverter}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-2 transition"
              >
                <FaRedo /> Reset
              </button>
              <button
                onClick={() => {}}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md flex items-center gap-2 transition"
              >
                Convert &gt;
              </button>
              
              {/* Export Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  disabled={!inputText}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 transition ${inputText ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed'}`}
                >
                  <FaFileExport /> Export <MdArrowDropDown />
                </button>
                {showExportDropdown && (
                  <div className="absolute z-10 mt-1 w-56 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600">
                    <div className="py-1">
                      <button 
                        onClick={() => { exportAsPDF(); setShowExportDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        Download PDF
                      </button>
                      <button 
                        onClick={() => { exportAsImage(); setShowExportDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        Download Image
                      </button>
                      <button 
                        onClick={() => { copyImageToClipboard(); setShowExportDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        Copy Image to Clipboard
                      </button>
                      <button 
                        onClick={() => { copyAsHTML(); setShowExportDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        Copy HTML
                      </button>
                      <button 
                        onClick={() => { copyAsMarkdown(); setShowExportDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        Copy Markdown
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => document.documentElement.requestFullscreen()}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-2 transition"
              >
                <FaExpand /> Full Screen
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-40 bg-gray-100 dark:text-white dark:bg-gray-800 p-4 text-center text-sm text-gray-600">
            <p>Improve your reading speed with bionic reading technology</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="bionicReadingConverter" />
    </>
  );
}